import ModernMUITable from "../../../../Components/ModernMUITable"
import { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from "react-router-dom";
import styled from 'styled-components';
import "@fontsource/source-sans-pro";
import '../../../../index.css';
import { FRONTEND_LINKS } from '../../../../Constants/FrontendLinks';
import { useEndpointProvider } from '../../../../Providers/EndpointProvider';
import { MobergButton, MobergButtonShape, MobergButtonVariant } from '../../../../Components/MobergButton/MobergButton';
import { MobergTheme } from '../../../../Components/MobergThemes/MobergColors';
import { useOnMount } from "../../../../Hooks/useOnMount";
import { localizeDate } from "../../../../Computation/utilFunctions";
import SubpageTabs from "../../../../Components/SubpageTabs/SubpageTabs";
import { useRecoilState } from "recoil";
import { currentPatientFileInfoAtom } from "../DataReview/Atoms/PatientFile";
import { AllUploadsPending } from "./Components/AllUploadsPending";
import { SubHeader } from "./Components/SubHeader";
import { useBackendLinksProvider } from "../../../../Providers/BackendLinksProvider";
import { useWorkspacesProvider } from "../../../../Providers/WorkspacesProvider";
import { PatientFileDropdown } from "../DataReview/Components/Modals/SelectPatientFilesModal/PatientFileDropdown";
import { Deployment, useEnvironmentVariablesProvider } from "../../../../Providers/EnvironmentVariablesProvider";
import { useDataReviewPermissions } from "../../../../Hooks/useDataReviewPermissions";

const FileSelectionDiv = styled.div`
	background: var(--color-surface-panel);
	border: 1px solid var(--color-table-border, #e0e0e0);
	border-radius: 4px;
	width: 400px;
	height: 160px;
	padding: 16px;
	flex-shrink: 0;
	display: flex;
	flex-direction: column;
	justify-content: space-around;
`

const ShimmerBox = styled.div`
	background: linear-gradient(90deg, var(--color-hover-neutral) 25%, var(--color-border-default) 50%, var(--color-hover-neutral) 75%);
	background-size: 200% 100%;
	animation: shimmer 1.5s infinite;
	border-radius: 4px;
	height: ${props => props.height || '40px'};
	width: ${props => props.width || '100%'};

	@keyframes shimmer {
		0% {
			background-position: -200% 0;
		}
		100% {
			background-position: 200% 0;
		}
	}
`

function determineSeverity(percentage) {
	if (percentage > 80) {
		return 'green'
	} else if (percentage < 25) {
		return 'red'
	} else {
		return 'yellow'
	}
}

// Are start_time and end_time below correct in timezone?
const modalityColumns = [
	{ field: "variable", flex: 0.4, headerName: "Variable", visible: true, minWidth: 120 },
	{ field: "min_start_time", flex: 0.4, headerName: "Start time", valueGetter: (value, row) => localizeDate(new Date(row.min_start_time), row.timezone), visible: true, minWidth: 170 },
	{ field: "max_end_time", flex: 0.4, headerName: "End time", valueGetter: (value, row) => localizeDate(new Date(row.max_end_time), row.timezone), visible: true, minWidth: 170 },
	{
		field: "time_recorded",
		flex: 0.4,
		headerName: "Time recorded",
		valueGetter: (value, row) => {
			const time_recorded = row.time_recorded.split(",")
			return `${time_recorded[0]} Day${time_recorded[0] > 1 || time_recorded[0] === 0 ? 's' : ''} ${time_recorded[1]} Hour${time_recorded[1] > 1 || time_recorded[1] === 0 ? 's' : ''} ${time_recorded[2]} Minute${time_recorded[2] > 1 || time_recorded[2] === 0 ? 's' : ''}`
		},
		visible: true,
		minWidth: 180
	},
	{
		field: "percent_recorded",
		flex: 0.4,
		headerName: "Percentage (%)",
		visible: true,
		minWidth: 190,
		valueGetter: (value, row) => `${parseFloat(row?.percent_recorded ?? 0).toFixed(2)}%`,
		cellClassName: (params) => `progress-${determineSeverity(params.row.percent_recorded)}`
	},
]

function toHHMMSS(time) {
	var sec_num = parseInt(time, 10); // don't forget the second param'
	var days = Math.floor(sec_num / (3600 * 24))
	var hours = Math.floor((sec_num - (days * 3600 * 24)) / 3600);
	var minutes = Math.floor((sec_num - (days * 3600 * 24) - (hours * 3600)) / 60);
	var seconds = sec_num - (days * 3600 * 24) - (hours * 3600) - (minutes * 60);

	return `${days} Day${days > 1 || days === 0 ? 's' : ''} ${hours} Hour${hours > 1 || hours === 0 ? 's' : ''} ${minutes} Minute${minutes > 1 || minutes === 0 ? 's' : ''}`
}

const VisualizePatient = (props) => {
	let navigate = useNavigate()
	const endpointProvider = useEndpointProvider()
	const workspaceProvider = useWorkspacesProvider()
	const { deployment } = useEnvironmentVariablesProvider()
	const isClinical = deployment === Deployment.CLINICAL
	const { canViewDataReview } = useDataReviewPermissions()
	const location = useLocation()
	const [patientFileInfo, setPatientFileInfo] = useRecoilState(currentPatientFileInfoAtom)
	const { LINKS } = useBackendLinksProvider()

	const query = new URLSearchParams(location.search);
	const UID = parseInt(query.get("patient_id"), 10)
	const [patientFiles, setPatientFiles] = useState([])
	const [patient, setPatient] = useState(location?.state)
	const [allUploadsPending, setAllUploadsPending] = useState(false)
	const [selectedFile, setSelectedFile] = useState(patientFiles?.length > 0 ? patientFiles[0] : undefined)
	
	// State for data review file selection
	const [selectedReviewFile, setSelectedReviewFile] = useState(patientFiles?.length > 0 ? patientFiles[0] : undefined)
	


	const patientId = patient?.patient_key // The actual patient database ID
	const patientIdentifier = patient?.patient_id // The visible patient identifier to the user
	const patientName = patient?.patient_name || "Unknown"
	const siteName = patient?.site_name
	const dataObjectId = selectedFile?.obj_id
	const dataObjectName = selectedFile?.obj_name

	const canViewData = (
		selectedReviewFile !== undefined &&
		selectedReviewFile.file_exists &&
		patientId !== undefined &&
		patientIdentifier !== undefined &&
		patientName !== undefined &&
		siteName !== undefined &&
		dataObjectId !== undefined &&
		dataObjectName !== undefined &&
		canViewDataReview
	) 

	// Set upload pending if none of them are complete
	const checkUploadsPending = () => endpointProvider.get(LINKS.DATA.UPLOAD.GET_PATIENT_UPLOADS, { patient_id: UID })
		.then(uploads => setAllUploadsPending(
			uploads
			&& uploads.length > 0
			&& uploads.filter(upload => upload.status === "SUCCESS").length === 0
		))
		.catch(err => console.error(err))

	useOnMount(() => {
		checkUploadsPending()
	})

	useEffect(() => {
		async function getPatient() {
			let body = {
				patient_primary_key: UID,
			}

			if (UID === undefined) {
				alert(`patient_id === undefined`)
				navigate(FRONTEND_LINKS.SUBPAGES.DATA.SUBPAGES.VISUALIZE.MAIN)
			}

			try {
				const patientData = await endpointProvider.post(LINKS.DATA.PROFILING.GET_PATIENT, body)
				return patientData
			} catch (error) {
				alert(`Patient id=${UID} does not exist.`)
				navigate(FRONTEND_LINKS.SUBPAGES.DATA.SUBPAGES.VISUALIZE.MAIN)
			}
		}

		if (!patient || Object.keys(patient).length === 0) {
			getPatient().then(patientData => {
				setPatient(patientData)
			})
		}
	}, [UID])

	// getPatientStartEndTimestamps:
	// Inputs:
	// 		-
	// Output:
	// 		- res (array):
	async function getPatientStartEndTimestamps(patient_id) {
		let body = {
			patient_ids: [patient_id],
		}

		return endpointProvider.post(LINKS.DATA.PROFILING.GET_PATIENT_FILE_START_END_TIMESTAMPS, body)
	}

	async function getPatientFileType(patient_id) {
		let body = {
			workspace_name: workspaceProvider.selectedWorkspace,
			patient_id: patient_id,
		}

		return endpointProvider.post(LINKS.DATA.PROFILING.GET_PATIENT_FILE_TYPE, body)
	}

	const [totalMonitoringTime, setTotalMonitoringTime] = useState()
	const [monitoringStartTime, setMonitoringStartTime] = useState()
	const [monitoringEndTime, setMonitoringEndTime] = useState()
	const [totalPatientStay, setTotalPatientStay] = useState()
	const [admissionTime, setAdmissionTime] = useState()
	const [dischargeTime, setDischargeTime] = useState()
	const [isLoadingTimestamps, setIsLoadingTimestamps] = useState(true)
	const [hasTimestampData, setHasTimestampData] = useState(false)
	const [isLoadingPatientFiles, setIsLoadingPatientFiles] = useState(true)

	useEffect(() => {
		if (UID === undefined) return

		// Reset loading state when UID changes
		setIsLoadingTimestamps(true)
		setHasTimestampData(false)
		setIsLoadingPatientFiles(true)

		getPatientFileType(UID).then(data => {
			const viewableFiles = data.filter(file => file.obj_type === "CNS" | file.obj_type === "Natus" | file.obj_type === "EDF")
			setPatientFiles(viewableFiles)
			setIsLoadingPatientFiles(false)
		}).catch(error => {
			console.error('Error fetching patient files:', error)
			setIsLoadingPatientFiles(false)
		})

		getPatientStartEndTimestamps(UID)
			.then(data => {
				setIsLoadingTimestamps(false)
				
				if (Object.keys(data).length === 0 && data.constructor === Object) {
					setHasTimestampData(false)
					return
				}
				
				setHasTimestampData(true)
				let admissionTime = Infinity
				let dischargeTime = -1
				let totalPatientStay = 0
				let monitoringStartTime = Infinity
				let monitoringEndTime = -1
				let totalMonitoringTime = 0
				let timezone
				for (const key in data) {
					timezone = data[key].timezone
					if (data[key].admission_time < admissionTime) {
						admissionTime = data[key].admission_time
					}
					if (data[key].discharge_time > dischargeTime) {
						dischargeTime = data[key].discharge_time
					}
					if (data[key].monitoring_start_time < monitoringStartTime) {
						monitoringStartTime = data[key].monitoring_start_time
					}
					if (data[key].monitoring_end_time > monitoringEndTime) {
						monitoringEndTime = data[key].monitoring_end_time
					}
				}
				totalPatientStay = dischargeTime - admissionTime
				totalMonitoringTime = monitoringEndTime - monitoringStartTime
				let startDate = localizeDate(new Date(admissionTime), timezone)
				setMonitoringStartTime(localizeDate(new Date(monitoringStartTime)), timezone)
				setAdmissionTime(startDate)
				let endDate = localizeDate(new Date(dischargeTime), timezone)
				setMonitoringEndTime(localizeDate(new Date(monitoringEndTime)), timezone)
				setDischargeTime(endDate)
				let formattedTotalTime = toHHMMSS(String(totalPatientStay / 1e3))
				setTotalMonitoringTime(toHHMMSS(String(totalMonitoringTime / 1e3)))
				setTotalPatientStay(formattedTotalTime)
			})
			.catch(e => {
				setIsLoadingTimestamps(false)
				setHasTimestampData(false)
				console.error('Error fetching timestamp data:', e)
			})
	}, [UID])


	function handleViewReviewFile() {
		if (!selectedReviewFile) return
		
		const fileName = selectedReviewFile.file_name || selectedReviewFile.obj_name || ''
		const fileId = selectedReviewFile.id || selectedReviewFile.obj_id
		
		navigate(
			`${FRONTEND_LINKS.SUBPAGES.DATA.SUBPAGES.REVIEW.MAIN}/${patientId}/${patientIdentifier}/${encodeURIComponent(siteName)}/${fileId}/${encodeURIComponent(fileName)}`,
		)
	}

	function refreshPatientFiles() {
		setIsLoadingPatientFiles(true)
		getPatientFileType(UID).then(data => {
			const viewableFiles = data.filter(file => file.obj_type === "CNS" || file.obj_type === "EDF")
			setPatientFiles(viewableFiles)
			setIsLoadingPatientFiles(false)
		}).catch(error => {
			console.error('Error refreshing patient files:', error)
			setIsLoadingPatientFiles(false)
		})
	}

	const handleSelectedFileChanged = useCallback(file => {
		setSelectedFile(file)
		setPatientFileInfo(previous => ({ ...previous, dataObjectId: file.obj_id }))
	}, [setPatientFileInfo])


	useEffect(() => {
		// The reason that we have to check if the patient files includes the selected file is because of object equality and immutability.
		// When we update the patientFiles from the database, they are brand new objects and have different locations in memory. Equals operator fails.
		// We have to re-set the selected file.
		if ((!selectedFile || !patientFiles?.includes(selectedFile)) && patientFiles?.length > 0) {
			handleSelectedFileChanged(patientFiles[0])
		}
		
		// Also set the review file if it's not set or not in the current patient files
		if ((!selectedReviewFile || !patientFiles?.includes(selectedReviewFile)) && patientFiles?.length > 0) {
			setSelectedReviewFile(patientFiles[0])
		}
	}, [handleSelectedFileChanged, patientFiles, selectedFile, selectedReviewFile])

	const canRenderTable = (
		patient?.project_id !== undefined &&
		patient?.site_id !== undefined &&
		patient?.patient_key !== undefined
	);



	const MainBody = () => {
		return (
			<div style={{
				padding: "0 32px",
				backgroundColor: "var(--color-surface-primary)",
				height: "calc(100vh - 200px)",
				minHeight: '500px',
				maxHeight: '80vh',
				overflow: "auto",
				display: "flex",
				flexDirection: "column"
			}}>
				<div style={{ display: "flex", gap: "16px", marginBottom: "8px" }}>
					<TimeRecordsDiv style={{ marginTop: '0px', flex: 1, maxWidth: 'calc(100% - 360px)', height: '160px' }}>
						{isLoadingTimestamps ? (
							// Show shimmer while loading
							<>
								<div>
									<p>Total patient stay: <strong><ShimmerBox height="16px" width="100px" /></strong></p>
									<p>Total monitoring time: <strong><ShimmerBox height="16px" width="100px" /></strong></p>
								</div>
								<div>
									<p>Admission time: <strong><ShimmerBox height="16px" width="100px" /></strong></p>
									<p>Monitoring start time: <strong><ShimmerBox height="16px" width="100px" /></strong></p>
								</div>
								<div>
									<p>Discharge time: <strong><ShimmerBox height="16px" width="100px" /></strong></p>
									<p>Monitoring end time: <strong><ShimmerBox height="16px" width="100px" /></strong></p>
								</div>
							</>
						) : !hasTimestampData ? (
							// Show no data message when no timestamp data is available
							<div style={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								height: '100%',
								width: '100%',
								color: 'var(--text-secondary-color)',
								fontStyle: 'italic'
							}}>
								No timestamp data available for this patient
							</div>
						) : (
							// Show actual data when available
							<>
								<div>
									<p>Total patient stay: <strong>{totalPatientStay}</strong></p>
									<p>Total monitoring time: <strong>{totalMonitoringTime}</strong></p>
								</div>
								<div>
									<p>Admission time: <strong>{admissionTime}</strong></p>
									<p>Monitoring start time: <strong>{monitoringStartTime}</strong></p>
								</div>
								<div>
									<p>Discharge time: <strong>{dischargeTime}</strong></p>
									<p>Monitoring end time: <strong>{monitoringEndTime}</strong></p>
								</div>
							</>
						)}
					</TimeRecordsDiv>
					
					<FileSelectionDiv>
						{isLoadingPatientFiles ? (
							// Show shimmer while loading
							<>
								<div style={{ marginBottom: "12px" }}>
									<ShimmerBox height="40px" width="100%" />
								</div>
								<ShimmerBox height="40px" width="100%" />
							</>
						) : patientFiles.length > 0 ? (
							// Show file dropdown and button when files are available
							<>
								<div style={{ marginBottom: "12px" }}>
									<PatientFileDropdown
										options={patientFiles}
										selectedValue={selectedReviewFile}
										onChange={setSelectedReviewFile}
										width={isClinical ? "100%" : "90%"}
										style={{ width: isClinical ? "100%" : "90%", marginRight: "8px" }}
										isClinical={isClinical}
									/>
								</div>
								<MobergButton
									onClick={handleViewReviewFile}
									theme={MobergTheme.BLUE}
									variant={MobergButtonVariant.FILLED}
									shape={MobergButtonShape.WIDE}
									style={{ width: "100%" }}
									disabled={!canViewData}
								>
									View file
								</MobergButton>
							</>
						) : (
							// Show no files message when no files are available
							<div style={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								height: '100%',
								width: '100%',
								color: 'var(--text-secondary-color)',
								fontStyle: 'italic',
								textAlign: 'center'
							}}>
								No viewable files available for this patient
							</div>
						)}
					</FileSelectionDiv>
				</div>
				{canRenderTable && (
					<div style={{ flex: 1, minHeight: 0, height: "100%" }}>
						<ModernMUITable
							key={"PatientModalitiesTable"}
							queryKey="patientModalities"
							endpoint={LINKS.DATA.UPLOAD.GET_MODALITY_TIME_BY_PATIENT}
							body={{ project_id: patient?.project_id, site_id: patient?.site_id, patient_id: patient?.patient_key }}
							columns={modalityColumns}
							tableToolbarProps={{ 
								mainTable: true,
								searchInput: { isShow: true },
								columnButton: { isShow: true },
								createButton: { isShow: false },
								deleteButton: { isShow: false },
								filterButton: { isShow: false },
								exportButton: { isShow: false }
							}}
							checkboxSelection={false}
							isRowSelectable={() => false}
							height="100%"
							paginationMode="server"
							sortingMode="server"
							supportsPageSize={true}
							initialState={{
								pagination: {
									paginationModel: { page: 0, pageSize: 10 }
								}
							}}
							dense={true}
						/>
					</div>
				)}
			</div>
		);
	}

	useEffect(() => {
		if (patient === undefined) {
			return
		}

		const body = {
			"patient_id": patient?.patient_key
		}

		endpointProvider.post(LINKS.VISUALIZE.GET_ADMISSION_INFO, body).then(result => {
			setPatientFileInfo(previous => ({...previous, isAdmitted: result.is_admitted }))
		})
	}, [LINKS, endpointProvider, patient, setPatientFileInfo])

	return (
		<div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
			<div style={{ display: "flex", flexDirection: "row", justifyContent: "space-between", padding: "6px 0px", marginTop: "8px", marginBottom: "16px", marginLeft: "32px", marginRight: "32px", borderBottom: "1px solid var(--color-border-default)", paddingBottom: "12px" }}>
				<SubHeader patient={patient} />
				<SubpageTabs UID={UID} patient={patient} currentTab={'Data'} />
			</div>
			
	

			<div style={{ flex: 1 }}>
				{allUploadsPending && !patientFileInfo.isAdmitted
					? <AllUploadsPending checkUploadsPending={checkUploadsPending} patient={patient} />
					: <MainBody />
				}
			</div>
		</div>
	)
}

const TimeRecordsDiv = styled.div`
	background: var(--color-surface-panel);
	border: 1px solid var(--color-table-border, #e0e0e0);
	border-radius: 4px;
	width: auto;
	height: auto;
	display: flex;
	position: relative;
	margin-bottom: 8px;
	padding: 20px;
	align-items: center;

	> div {
		flex: 1;
		min-width: 0;

		&:not(:last-child) {
			margin-right: 32px;
			border-right: 1px solid var(--color-border-default);
			padding-right: 32px;
		}
	}

	p {
		font-family: "Source Sans Pro", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
		font-style: normal;
		font-weight: 400;
		font-size: 14px;
		line-height: 1.5;
		color: var(--text-secondary-color);
		margin: 0 0 8px 0;

		&:last-child {
			margin-bottom: 0;
		}

		strong {
			font-weight: 600;
			color: var(--color-text-primary);
			display: block;
			margin-top: 2px;
			font-size: 16px;
		}
	}
`

export default VisualizePatient